import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, Upload, Loader2, Image as ImageIcon, Search, Copy, Check, ZoomIn, X, Award, Eye, EyeOff } from 'lucide-react';
import logger from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'all', label: 'Toutes' },
  { value: 'conferences', label: 'Conférences' },
  { value: 'exposition', label: 'Exposition' },
  { value: 'networking', label: 'Networking' },
  { value: 'partenaires', label: 'Partenaires' },
  { value: 'editions', label: 'Éditions précédentes' },
  { value: 'coulisses', label: 'Coulisses' },
  { value: 'general', label: 'Général' },
];

export default function ManageGallery() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [category, setCategory] = useState('general');
  const [filterCat, setFilterCat] = useState('all');
  const [filterType, setFilterType] = useState('all'); // all | logos | photos
  const [uploadAsLogo, setUploadAsLogo] = useState(false);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(null);
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery/all`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleMultiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);
    let done = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', file.name.replace(/\.[^/.]+$/, ''));
        fd.append('category', category);
        fd.append('is_logo', uploadAsLogo ? 'true' : 'false');
        await axios.post(`${API}/gallery`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        done++;
        setUploadProgress(Math.round((done / files.length) * 100));
      } catch (err) {
        logger.error('Upload failed:', err);
        toast.error(`Erreur: ${file.name}`);
      }
    }
    toast.success(`${done}/${files.length} ${uploadAsLogo ? 'logo(s)' : 'image(s)'} ajouté(s)`);
    setUploading(false);
    setUploadProgress(0);
    e.target.value = '';
    fetchItems();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers });
      toast.success('Image supprimée');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const toggleVisibility = async (img) => {
    const newState = img.is_active === false ? true : false;
    try {
      await axios.patch(`${API}/gallery/${img.id}`, { is_active: newState }, { headers });
      toast.success(newState ? 'Image rendue visible' : 'Image masquée');
      fetchItems();
    } catch (e) { logger.error('Toggle visibility failed:', e); toast.error('Erreur'); }
  };

  const copyUrl = (img) => {
    const url = `${BACKEND_URL}${img.url}`;
    navigator.clipboard.writeText(url);
    setCopied(img.id);
    toast.success('URL copiée');
    setTimeout(() => setCopied(null), 2000);
  };

  const filtered = useMemo(() => items.filter(img => {
    const matchCat = filterCat === 'all' || img.category === filterCat;
    const matchSearch = !search || img.title?.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || (filterType === 'logos' ? img.is_logo : !img.is_logo);
    return matchCat && matchSearch && matchType;
  }), [items, filterCat, search, filterType]);

  const logoCount = useMemo(() => items.filter(i => i.is_logo).length, [items]);
  const photoCount = useMemo(() => items.filter(i => !i.is_logo).length, [items]);

  const catCounts = useMemo(() => items.reduce((acc, img) => {
    acc[img.category] = (acc[img.category] || 0) + 1;
    return acc;
  }, {}), [items]);

  return (
    <div data-testid="manage-gallery">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Bibliothèque de Médias</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} fichier(s) — {photoCount} photo(s), {logoCount} logo(s) — {filtered.length} affiché(s)</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-40 text-sm" data-testid="upload-category-select"><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent>{CATEGORIES.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          {/* "Ceci est un logo" checkbox */}
          <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-all ${uploadAsLogo ? 'bg-[#FFF7E5] border-[#F5A623] text-[#1E3A5F]' : 'bg-white border-[#E1E8F0] text-[#6B7C93] hover:border-[#94A3B8]'}`} data-testid="upload-as-logo-toggle">
            <input
              type="checkbox"
              checked={uploadAsLogo}
              onChange={(e) => setUploadAsLogo(e.target.checked)}
              className="accent-[#F5A623] w-4 h-4"
              data-testid="upload-as-logo-checkbox"
            />
            <Award className={`w-4 h-4 ${uploadAsLogo ? 'text-[#F5A623]' : 'text-[#94A3B8]'}`} />
            <span className="text-sm font-medium whitespace-nowrap">Ceci est un logo</span>
          </label>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-[#1E3A5F] hover:bg-[#152A45]"
            data-testid="gallery-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? `${uploadProgress}%` : 'Ajouter des images'}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleMultiUpload}
            disabled={uploading}
            data-testid="gallery-upload-input"
          />
        </div>
      </div>

      {/* Type tabs (Photos / Logos) */}
      <div className="flex items-center gap-2 mb-4 border-b border-[#E1E8F0] pb-3">
        {[
          { value: 'all', label: 'Tout', count: items.length },
          { value: 'photos', label: 'Photos', count: photoCount },
          { value: 'logos', label: 'Logos', count: logoCount },
        ].map(t => (
          <button
            key={t.value}
            onClick={() => setFilterType(t.value)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${filterType === t.value ? 'bg-[#1E3A5F] text-white' : 'bg-transparent text-[#6B7C93] hover:bg-[#F0F2F5]'}`}
            data-testid={`type-filter-${t.value}`}
          >
            {t.label} <span className="text-xs opacity-75">({t.count})</span>
          </button>
        ))}
        {filterType === 'logos' && (
          <span className="ml-3 text-xs text-[#94A3B8] italic">Les logos sont exclus de la galerie publique du site.</span>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="pl-10 text-sm"
            data-testid="gallery-search"
          />
        </div>
        <div className="flex gap-1.5">
          {CATEGORIES.map(c => {
            // Build category label with count suffix — replaces nested ternary for clarity.
            let suffix = '';
            if (c.value === 'all') suffix = ` (${items.length})`;
            else if (catCounts[c.value]) suffix = ` (${catCounts[c.value]})`;
            return (
              <button
                key={c.value}
                onClick={() => setFilterCat(c.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterCat === c.value ? 'bg-[#1E3A5F] text-white' : 'bg-[#F0F2F5] text-[#6B7C93] hover:bg-[#E1E8F0]'}`}
                data-testid={`filter-${c.value}`}
              >
                {c.label}{suffix}
              </button>
            );
          })}
        </div>
      </div>

      {/* Upload progress bar */}
      {uploading && (
        <div className="mb-4 bg-[#F0F2F5] rounded-full h-2 overflow-hidden">
          <div className="h-full bg-[#F5A623] transition-all duration-300 rounded-full" style={{ width: `${uploadProgress}%` }} />
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {filtered.map((img) => (
          <div key={img.id} className={`relative group rounded-xl overflow-hidden shadow-sm bg-white border border-[#E1E8F0] ${img.is_active === false ? 'opacity-60' : ''}`} data-testid={`gallery-item-${img.id}`}>
            <img src={`${BACKEND_URL}${img.url}`} alt={img.title} className={`w-full h-32 cursor-pointer ${img.is_logo ? 'object-contain p-3 bg-[#F7F9FC]' : 'object-cover'}`} onClick={() => setPreview(img)} />
            {img.is_logo && (
              <div className="absolute top-2 left-2 bg-[#F5A623] text-[#1E3A5F] text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`logo-badge-${img.id}`}>
                <Award className="w-3 h-3" /> Logo
              </div>
            )}
            {img.is_active === false && (
              <div className="absolute top-2 right-2 bg-[#1E3A5F] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`hidden-badge-${img.id}`}>
                <EyeOff className="w-3 h-3" /> Masquée
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
              <button onClick={() => setPreview(img)} className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors pointer-events-auto" title="Aperçu">
                <ZoomIn className="w-4 h-4" />
              </button>
              <button onClick={() => copyUrl(img)} className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors pointer-events-auto" title="Copier l'URL">
                {copied === img.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <button onClick={() => toggleVisibility(img)} className="p-1.5 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors pointer-events-auto" title={img.is_active === false ? 'Afficher' : 'Masquer'} data-testid={`toggle-visibility-${img.id}`}>
                {img.is_active === false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => handleDelete(img.id)} className="p-1.5 rounded-full bg-white/20 text-red-300 hover:bg-red-500/40 transition-colors pointer-events-auto" data-testid={`delete-gallery-${img.id}`} title="Supprimer">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="p-2">
              <p className="text-xs font-medium text-[#1E3A5F] truncate">{img.title}</p>
              <p className="text-[10px] text-[#94A3B8] capitalize">{img.category}</p>
            </div>
          </div>
        ))}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 text-[#6B7C93]">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="font-medium">Aucune image</p>
          <p className="text-sm mt-1">Cliquez sur "Ajouter des images" pour commencer</p>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="bg-white max-w-3xl p-0 overflow-hidden">
          {preview && (
            <>
              <img src={`${BACKEND_URL}${preview.url}`} alt={preview.title} className="w-full max-h-[70vh] object-contain bg-[#F0F2F5]" />
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#1E3A5F]">{preview.title}</p>
                  <p className="text-sm text-[#6B7C93] capitalize">{preview.category}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => copyUrl(preview)} data-testid="preview-copy-url">
                    {copied === preview.id ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    Copier l'URL
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { handleDelete(preview.id); setPreview(null); }} className="text-red-500 border-red-200 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1" />Supprimer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
